import Button from "@/components/ui/button"
import i18n from "@/utils/i18n";
import { Close as CloseIcon, Check as CheckIcon } from "@/components/icons"
import {
  SquareRoundedCheck,
  CircleDashed,
  Clock,
  Calendar,
} from "@/components/icons"

import "./index.scss"

import { FC, useEffect } from "react"
import { createPortal } from "react-dom"

export interface ExamInfo {
  passingGrade: number
  questionCount: number
  duration: string
  description: string
}

export interface PollInfo {
  pollName: string
  questionCount: number
  duration?: string
  description: string
  startDate?: string
  endDate?: string
}

export interface ConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: React.ReactNode
  confirmText?: string
  cancelText?: string
  loading?: boolean
  examInfo?: ExamInfo
  pollInfo?: PollInfo
  /** "danger" = silme/iptal gibi yıkıcı işlemler için #e7212b (primary) renk */
  confirmVariant?: "default" | "danger"
}

const ConfirmModal: FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  loading = false,
  examInfo,
  pollInfo,
  confirmVariant = "default",
}) => {
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey)
      document.body.style.overflow = "hidden"
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey)
      document.body.style.overflow = ""
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const isDanger = confirmVariant === "danger"

  return createPortal(
    <div className="confirm-modal-overlay" onClick={onClose}>
      <div className={`confirm-modal ${isDanger ? "confirm-modal--danger" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div className="confirm-modal__header">
          <div className={`confirm-modal__icon ${isDanger ? "confirm-modal__icon--danger" : ""}`}>
            <CheckIcon width={24} height={24} color={isDanger ? "#e7212b" : undefined} />
          </div>
          <h3 className="confirm-modal__title">{title}</h3>
          <button className="confirm-modal__close" onClick={onClose}>
            <CloseIcon width={20} height={20} color="#707A8F" />
          </button>
        </div>

        <div className="confirm-modal__body">
          {examInfo ? (
            <>
              <div className="confirm-modal__exam-stats">
                <div className="confirm-modal__stat-card">
                  <div className="confirm-modal__stat-icon confirm-modal__stat-icon--success">
                    <SquareRoundedCheck width={24} height={24} />
                  </div>
                  <div className="confirm-modal__stat-content">
                    <span className="confirm-modal__stat-value">{examInfo.passingGrade}</span>
                    <span className="confirm-modal__stat-label">{i18n.t("examManagement.detail.passingScore")}</span>
                  </div>
                </div>
                <div className="confirm-modal__stat-card">
                  <div className="confirm-modal__stat-icon confirm-modal__stat-icon--purple">
                    <CircleDashed width={24} height={24} />
                  </div>
                  <div className="confirm-modal__stat-content">
                    <span className="confirm-modal__stat-value">{examInfo.questionCount}</span>
                    <span className="confirm-modal__stat-label">Soru</span>
                  </div>
                </div>
                <div className="confirm-modal__stat-card">
                  <div className="confirm-modal__stat-icon confirm-modal__stat-icon--blue">
                    <Clock width={24} height={24} />
                  </div>
                  <div className="confirm-modal__stat-content">
                    <span className="confirm-modal__stat-value">{examInfo.duration}</span>
                    <span className="confirm-modal__stat-label">{i18n.t("createEducation.sidebar.duration")}</span>
                  </div>
                </div>
              </div>
              <div className="confirm-modal__exam-description">
                {examInfo.description}
              </div>
            </>
          ) : pollInfo ? (
            <>
              <h4 className="confirm-modal__poll-name">{pollInfo.pollName}</h4>
              <div className="confirm-modal__exam-stats">
                <div className="confirm-modal__stat-card">
                  <div className="confirm-modal__stat-icon confirm-modal__stat-icon--purple">
                    <CircleDashed width={24} height={24} />
                  </div>
                  <div className="confirm-modal__stat-content">
                    <span className="confirm-modal__stat-value">{pollInfo.questionCount}</span>
                    <span className="confirm-modal__stat-label">Soru</span>
                  </div>
                </div>
                {pollInfo.duration && (
                  <div className="confirm-modal__stat-card">
                    <div className="confirm-modal__stat-icon confirm-modal__stat-icon--blue">
                      <Clock width={24} height={24} />
                    </div>
                    <div className="confirm-modal__stat-content">
                      <span className="confirm-modal__stat-value">{pollInfo.duration}</span>
                      <span className="confirm-modal__stat-label">{i18n.t("createEducation.sidebar.duration")}</span>
                    </div>
                  </div>
                )}
              </div>
              {(pollInfo.startDate || pollInfo.endDate) && (
                <div className="confirm-modal__poll-dates">
                  {pollInfo.startDate && (
                    <div className="confirm-modal__date-item">
                      <Calendar width={16} height={16} />
                      <span className="confirm-modal__date-label">{i18n.t("confirmModal.startLabel")}</span>
                      <span className="confirm-modal__date-value">{pollInfo.startDate}</span>
                    </div>
                  )}
                  {pollInfo.endDate && (
                    <div className="confirm-modal__date-item">
                      <Calendar width={16} height={16} />
                      <span className="confirm-modal__date-label">{i18n.t("confirmModal.endLabel")}</span>
                      <span className="confirm-modal__date-value">{pollInfo.endDate}</span>
                    </div>
                  )}
                </div>
              )}
              <div className="confirm-modal__exam-description">
                {pollInfo.description}
              </div>
            </>
          ) : (
            <div className="confirm-modal__message">{message}</div>
          )}
        </div>

        <div className="confirm-modal__footer">
          <Button
            variant="contained"
            color="info"
            onClick={onClose}
            disabled={loading}
            startIcon={<CloseIcon width={16} height={16} color="#212B36" />}
          >
            {cancelText ?? i18n.t("cancel")}
          </Button>
          <Button
            variant="contained"
            color={isDanger ? "primary" : "success"}
            onClick={onConfirm}
            disabled={loading}
            startIcon={<CheckIcon width={16} height={16} color="#FFFFFF" />}
          >
            {loading ? i18n.t("loading") : (confirmText ?? i18n.t("confirm"))}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default ConfirmModal
